import React, { forwardRef } from 'react';

import type { ButtonProps, CircularProgressProps } from '@mui/material';
import { Button, CircularProgress } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { lighten } from '@mui/material/styles';
import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';

interface StyleProps {
  buttonColor: ButtonProps['color'];
  marginLeft: number;
  marginTop: number;
  fullWidth?: boolean;
}

const useStyles = makeStyles<StyleProps>()(
  (theme, { buttonColor, fullWidth, marginLeft, marginTop }) => ({
    contained: {
      '&.Mui-disabled': {
        backgroundColor: getDisabledContainedBackground(theme, buttonColor),
        color: getDisabledContainedColor(theme, buttonColor),
      },
    },
    containedPrimary: {
      /**
       * MUI v9 removed variant+color slot keys (containedPrimary, outlinedPrimary, etc.)
       * from ButtonClasses, so we can't override via the `classes` prop anymore.
       * MUI's auto-generated `.MuiButton-contained.MuiButton-colorPrimary` selector
       * has specificity 0,2,0 — match it by chaining `&.MuiButton-contained`
       * so emotion's later insertion order lets our brand color win.
       */
      '&.MuiButton-contained': {
        '&.Mui-disabled': {
          backgroundColor: getDisabledContainedBackground(theme, buttonColor),
          color: getDisabledContainedColor(theme, buttonColor),
        },
        '&:active': {
          backgroundColor: theme.appColors.buttonPressed,
        },
        '&:hover': {
          backgroundColor: theme.appColors.buttonHover,
        },
        backgroundColor: theme.appColors.primary,
        color: theme.palette.common.white,
      },
    },
    disabled: {},
    outlinedPrimary: {
      '&.MuiButton-outlined': {
        '&.Mui-disabled': {
          border: `1px solid ${theme.appColors.halfPrimary}`,
          color: theme.appColors.primary,
          opacity: 0.5,
        },
        '&.Mui-selected': {
          '&:hover': {
            backgroundColor: theme.appColors.selectedBackground,
          },
          backgroundColor: theme.appColors.selectedBackground,
          color: theme.appColors.primary,
        },
        '&:active': {
          backgroundColor: theme.appColors.selectedBackgroundNav,
        },
        '&:hover': {
          backgroundColor: theme.appColors.hoverBackground,
          border: `1px solid ${theme.appColors.primary}`,
        },
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${theme.appColors.primary}`,
        color: theme.appColors.primary,
      },
    },
    progress: {
      left: '50%',
      marginLeft,
      marginTop,
      position: 'absolute',
      top: '50%',
    },
    root: {
      borderRadius: 16,
      fontFamily: theme.typography.fontFamily,
      fontSize: 14,
      fontWeight: 600,
      height: 32,
      lineHeight: 1.3,
      textTransform: 'none',
      whiteSpace: 'nowrap',
      width: '100%',
    },
    selected: {},
    wrapper: {
      display: 'inline-block',
      height: 'fit-content',
      position: 'relative',
      width: fullWidth ? '100%' : 'fit-content',
    },
  })
);

export interface AlineButtonProps extends ButtonProps {
  buttonClassName?: string;
  component?: React.ElementType;
  loading?: boolean;
  progressClassName?: string;
  rel?: string;
  selected?: boolean;
  target?: string;
  to?: string;
}

/**
 * The Button component is a wrapper around [Material-UI's Button](https://material-ui.com/components/buttons/) using Aline's design.
 * We pass all `props` from our Button to the Material-UI `Button` component.
 * Not all variations of a `Button` are shown below.
 * See [Material-UI Button Examples](https://material-ui.com/components/buttons/) and [Material-UI Button API](https://material-ui.com/api/button/) for additional variations of a `Button` we can create.
 * Note: We have to expose a `component` and `to` prop because of TS errors.  Usually, those props are available on ButtonProps already.  See https://github.com/mui-org/material-ui/issues/16846.
 */
export const AlineButton = forwardRef(function AlineButton(
  props: AlineButtonProps,
  ref: React.Ref<HTMLButtonElement>
): JSX.Element {
  const {
    buttonClassName,
    children,
    className,
    classes,
    color = 'inherit',
    component = 'button',
    disableFocusRipple = true,
    disableRipple = true,
    disabled = false,
    fullWidth = false,
    loading,
    progressClassName,
    selected,
    size = 'medium',
    variant = 'text',
    ...rest
  } = props;

  const { marginLeft, marginTop, progressSize } = progressPositionDetails(size);
  const { classes: internalClasses } = useStyles({
    buttonColor: color,
    fullWidth,
    marginLeft,
    marginTop,
  });

  return (
    <div className={clsx(internalClasses.wrapper, className)}>
      <Button
        ref={ref}
        className={clsx(
          buttonClassName,
          selected && internalClasses.selected,
          color === 'primary' && variant === 'contained' && internalClasses.containedPrimary,
          color === 'primary' && variant === 'outlined' && internalClasses.outlinedPrimary
        )}
        classes={{
          contained: internalClasses.contained,
          disabled: internalClasses.disabled,
          root: internalClasses.root,
          ...classes,
        }}
        color={color}
        component={component}
        disableFocusRipple={disableFocusRipple}
        disableRipple={disableRipple}
        disabled={loading || disabled}
        size={size}
        variant={variant}
        {...rest}
      >
        {children}
      </Button>
      {loading && (
        <CircularProgress
          className={clsx(internalClasses.progress, progressClassName)}
          color={progressColor(color)}
          size={progressSize}
        />
      )}
    </div>
  );
});

export function progressColor(buttonColor: ButtonProps['color']): CircularProgressProps['color'] {
  switch (buttonColor) {
    case 'inherit':
      return 'inherit';
    default:
      return buttonColor;
  }
}

function getDisabledContainedBackground(theme: Theme, buttonColor: ButtonProps['color']) {
  const color = getPaletteMainColor(theme, buttonColor);
  if (!color) return theme.palette.grey[300];

  return lighten(color, 0.45);
}

function getDisabledContainedColor(theme: Theme, buttonColor: ButtonProps['color']) {
  const color = getPaletteContrastText(theme, buttonColor);
  return color ?? theme.palette.text.disabled;
}

function getPaletteMainColor(theme: Theme, buttonColor: ButtonProps['color']) {
  switch (buttonColor) {
    case 'primary':
      return theme.palette.primary.main;
    case 'secondary':
      return theme.palette.secondary.main;
    case 'error':
      return theme.palette.error.main;
    case 'info':
      return theme.palette.info.main;
    case 'success':
      return theme.palette.success.main;
    case 'warning':
      return theme.palette.warning.main;
    default:
      return null;
  }
}

function getPaletteContrastText(theme: Theme, buttonColor: ButtonProps['color']) {
  switch (buttonColor) {
    case 'primary':
      return theme.palette.primary.contrastText;
    case 'secondary':
      return theme.palette.secondary.contrastText;
    case 'error':
      return theme.palette.error.contrastText;
    case 'info':
      return theme.palette.info.contrastText;
    case 'success':
      return theme.palette.success.contrastText;
    case 'warning':
      return theme.palette.warning.contrastText;
    default:
      return null;
  }
}

export function progressPositionDetails(buttonSize: ButtonProps['size']): {
  marginLeft: number;
  marginTop: number;
  progressSize: number;
} {
  switch (buttonSize) {
    case 'small':
      return { marginLeft: -8, marginTop: -8, progressSize: 16 };
    case 'large':
      return { marginLeft: -12, marginTop: -12, progressSize: 24 };
    case 'medium':
    default:
      return { marginLeft: -10, marginTop: -10, progressSize: 20 };
  }
}
